import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Star, Upload, ArrowLeft, Save, Award, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useProgramCertificates,
  useSaveProgramCertificate,
  useDeleteProgramCertificate,
  type DbCertificate,
} from '@/hooks/useProgramManagement';
import { emptyDiploma, type DiplomaConfig, useCertificateConfig, normalizeDiplomaLayout, DEFAULT_DIPLOMA_LAYOUT } from '@/hooks/useCertificateConfig';
import { useCompetencies } from '@/hooks/useCompetencies';
import { DiplomaLayoutEditor } from '@/components/admin/DiplomaLayoutEditor';

interface Props {
  programId: string | null;
}

interface EditingCert {
  id?: string;
  private_name: string;
  is_default: boolean;
  diploma_a: DiplomaConfig;
  diploma_b: DiplomaConfig;
}

function dbToEditing(c: DbCertificate): EditingCert {
  const buildDiploma = (which: 'a' | 'b'): DiplomaConfig => ({
    name: (c as any)[`diploma_${which}_name`] ?? '',
    description: (c as any)[`diploma_${which}_description`] ?? '',
    image_url: (c as any)[`diploma_${which}_image_url`] ?? null,
    skills: Array.isArray((c as any)[`diploma_${which}_skills`]) ? (c as any)[`diploma_${which}_skills`] : [],
    entities: (c as any)[`diploma_${which}_entities`] ?? '',
    additional_info: (c as any)[`diploma_${which}_additional_info`] ?? '',
    additional_url: (c as any)[`diploma_${which}_additional_url`] ?? '',
    priority: (c as any)[`diploma_${which}_priority`] ?? (which === 'a' ? 1 : 2),
    require_content: (c as any)[`diploma_${which}_require_content`] ?? (which === 'a'),
    min_content_percent: (c as any)[`diploma_${which}_min_content_percent`] ?? 100,
    require_deliverables: (c as any)[`diploma_${which}_require_deliverables`] ?? (which === 'b'),
    min_deliverables_percent: (c as any)[`diploma_${which}_min_deliverables_percent`] ?? 100,
    require_project: (c as any)[`diploma_${which}_require_project`] ?? (which === 'b'),
    require_project_approved: (c as any)[`diploma_${which}_require_project_approved`] ?? false,
    require_project_answered: (c as any)[`diploma_${which}_require_project_answered`] ?? false,
    require_lab: (c as any)[`diploma_${which}_require_lab`] ?? (which === 'b'),
    layout: normalizeDiplomaLayout((c as any)[`diploma_${which}_layout`] ?? DEFAULT_DIPLOMA_LAYOUT),
  });
  return {
    id: c.id,
    private_name: c.private_name || '',
    is_default: !!c.is_default,
    diploma_a: buildDiploma('a'),
    diploma_b: buildDiploma('b'),
  };
}

function DiplomaEditor({
  value,
  onChange,
  skillOptions,
  uploadImage,
  letter,
}: {
  value: DiplomaConfig;
  onChange: (next: DiplomaConfig) => void;
  skillOptions: string[];
  uploadImage: (f: File) => Promise<string>;
  letter: 'A' | 'B';
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [skillInput, setSkillInput] = useState('');

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadImage(f);
      onChange({ ...value, image_url: url });
    } catch (err: any) {
      toast({ title: 'Error subiendo imagen', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 pt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Datos del Diploma {letter}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre público (en diploma)</Label>
              <Input value={value.name} onChange={e => onChange({ ...value, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={value.description} onChange={e => onChange({ ...value, description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Entidades emisoras</Label>
              <Input value={value.entities} onChange={e => onChange({ ...value, entities: e.target.value })} placeholder="Ej: LIDER IA, Universidad…" />
            </div>
            <div className="space-y-2">
              <Label>Información adicional</Label>
              <Textarea value={value.additional_info} onChange={e => onChange({ ...value, additional_info: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>URL adicional</Label>
              <Input value={value.additional_url} onChange={e => onChange({ ...value, additional_url: e.target.value })} placeholder="https://…" />
            </div>
            <div className="space-y-2">
              <Label>Prioridad (mayor gana cuando se cumplen ambos)</Label>
              <Input
                type="number"
                min={1}
                value={value.priority}
                onChange={e => onChange({ ...value, priority: parseInt(e.target.value) || 1 })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Imagen base del diploma</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {value.image_url ? (
              <div className="relative aspect-[16/11] bg-muted rounded-lg overflow-hidden border border-border">
                <img src={value.image_url} alt="Diploma base" className="w-full h-full object-contain" />
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={() => onChange({ ...value, image_url: null })}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="aspect-[16/11] bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">
                Sin imagen base
              </div>
            )}
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {value.image_url ? 'Reemplazar imagen' : 'Subir imagen (PNG)'}
            </Button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFile} />
            <p className="text-xs text-muted-foreground">
              La imagen se usa como fondo del PDF; los datos del participante se superponen al generar.
            </p>
          </CardContent>
        </Card>
      </div>

      <DiplomaLayoutEditor
        imageUrl={value.image_url}
        layout={value.layout}
        onChange={(next) => onChange({ ...value, layout: next })}
      />

      <Card>
        <CardHeader><CardTitle className="text-base">Competencias acreditadas</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5 min-h-[28px]">
            {(value.skills || []).map((s: any, idx: number) => (
              <Badge key={`${s?.name || s}-${idx}`} variant="secondary" className="gap-1">
                {typeof s === 'string' ? s : s?.name}
                <button
                  className="ml-1 hover:text-destructive"
                  onClick={() => onChange({ ...value, skills: value.skills.filter((_: any, i: number) => i !== idx) })}
                >×</button>
              </Badge>
            ))}
            {value.skills.length === 0 && <span className="text-xs text-muted-foreground">Sin competencias añadidas</span>}
          </div>
          <div className="flex gap-2">
            <Select value={skillInput} onValueChange={setSkillInput}>
              <SelectTrigger className="flex-1"><SelectValue placeholder="Seleccionar competencia" /></SelectTrigger>
              <SelectContent>
                {skillOptions
                  .filter(s => !value.skills.some((sk: any) => (typeof sk === 'string' ? sk : sk?.name) === s))
                  .map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (!skillInput) return;
                onChange({ ...value, skills: [...value.skills, { name: skillInput }] });
                setSkillInput('');
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Requisitos para emitir</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-medium">Contenido obligatorio de la ruta</Label>
              <Switch checked={value.require_content} onCheckedChange={(v) => onChange({ ...value, require_content: v })} />
            </div>
            {value.require_content && (
              <div className="pl-1 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">% mínimo completado</span>
                  <span className="font-semibold">{value.min_content_percent}%</span>
                </div>
                <Slider
                  value={[value.min_content_percent]}
                  onValueChange={([v]) => onChange({ ...value, min_content_percent: v })}
                  min={0} max={100} step={5}
                />
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-medium">Entregables</Label>
              <Switch checked={value.require_deliverables} onCheckedChange={(v) => onChange({ ...value, require_deliverables: v })} />
            </div>
            {value.require_deliverables && (
              <div className="pl-1 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">% mínimo entregado</span>
                  <span className="font-semibold">{value.min_deliverables_percent}%</span>
                </div>
                <Slider
                  value={[value.min_deliverables_percent]}
                  onValueChange={([v]) => onChange({ ...value, min_deliverables_percent: v })}
                  min={0} max={100} step={5}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <Label className="font-medium">Proyecto entregado</Label>
            <Switch checked={value.require_project} onCheckedChange={(v) => onChange({ ...value, require_project: v })} />
          </div>

          <div className="flex items-center justify-between">
            <Label className="font-medium">Proyecto aprobado por super admin</Label>
            <Switch checked={value.require_project_approved} onCheckedChange={(v) => onChange({ ...value, require_project_approved: v })} />
          </div>

          <div className="flex items-center justify-between">
            <Label className="font-medium">Preguntas LIDER-IA del proyecto respondidas</Label>
            <Switch checked={value.require_project_answered} onCheckedChange={(v) => onChange({ ...value, require_project_answered: v })} />
          </div>

          <div className="flex items-center justify-between">
            <Label className="font-medium">Laboratorio de retos completado</Label>
            <Switch checked={value.require_lab} onCheckedChange={(v) => onChange({ ...value, require_lab: v })} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ProgramCertificateTab({ programId }: Props) {
  const { data: certificates = [], isLoading } = useProgramCertificates(programId);
  const saveCert = useSaveProgramCertificate();
  const deleteCert = useDeleteProgramCertificate();
  const { uploadDiplomaImage } = useCertificateConfig();
  const { data: competencies = [] } = useCompetencies();
  const skillOptions = competencies.map(c => c.name);

  const [editing, setEditing] = useState<EditingCert | null>(null);
  const [activeTab, setActiveTab] = useState<'a' | 'b'>('a');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  if (!programId) {
    return (
      <Card><CardContent className="p-8 text-center text-muted-foreground">
        Guarda la ruta primero para configurar sus certificados.
      </CardContent></Card>
    );
  }

  const handleNew = () => {
    setEditing({
      private_name: '',
      is_default: certificates.length === 0,
      diploma_a: emptyDiploma('A'),
      diploma_b: emptyDiploma('B'),
    });
    setActiveTab('a');
  };

  const handleEdit = (c: DbCertificate) => {
    setEditing(dbToEditing(c));
    setActiveTab('a');
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.private_name.trim()) {
      toast({ title: 'Falta nombre interno', description: 'Indica un nombre privado para identificar el certificado.', variant: 'destructive' });
      return;
    }
    if (!editing.diploma_a.name.trim()) {
      toast({ title: 'Falta Diploma A', description: 'El Diploma A debe tener al menos un nombre público.', variant: 'destructive' });
      return;
    }
    const aActive = editing.diploma_a.require_content || editing.diploma_a.require_deliverables || editing.diploma_a.require_project || editing.diploma_a.require_lab;
    const bActive = editing.diploma_b.name.trim() && (editing.diploma_b.require_content || editing.diploma_b.require_deliverables || editing.diploma_b.require_project || editing.diploma_b.require_lab);
    if (aActive && bActive && editing.diploma_a.priority === editing.diploma_b.priority) {
      toast({ title: 'Prioridades duplicadas', description: 'Si ambos diplomas están activos, sus prioridades deben ser distintas.', variant: 'destructive' });
      return;
    }

    try {
      await saveCert.mutateAsync({
        programId: programId!,
        certificate: {
          id: editing.id,
          private_name: editing.private_name,
          is_default: editing.is_default,
          public_name: editing.diploma_a.name || editing.private_name,
          diploma_a: editing.diploma_a,
          diploma_b: editing.diploma_b,
        },
      });
      toast({ title: 'Certificado guardado' });
      setEditing(null);
    } catch (e: any) {
      toast({ title: 'Error al guardar', description: e.message, variant: 'destructive' });
    }
  };

  const handleMakeDefault = async (cert: DbCertificate) => {
    try {
      await saveCert.mutateAsync({
        programId: programId!,
        certificate: { ...dbToEditing(cert), is_default: true } as any,
      });
      toast({ title: 'Marcado como por defecto' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteCert.mutateAsync({ certificateId: confirmDelete, programId: programId! });
      toast({ title: 'Certificado eliminado' });
      setConfirmDelete(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // ── Edit view ──
  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver a la lista
            </Button>
            <h3 className="text-lg font-semibold">
              {editing.id ? 'Editar certificado' : 'Nuevo certificado'}
            </h3>
          </div>
          <Button onClick={handleSave} disabled={saveCert.isPending}>
            {saveCert.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Datos generales</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre interno (no aparece en el diploma)</Label>
                <Input
                  value={editing.private_name}
                  onChange={e => setEditing({ ...editing, private_name: e.target.value })}
                  placeholder="Ej: Certificación LIDER IA — Cohorte 2026"
                />
              </div>
              <div className="flex items-end gap-3 pb-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editing.is_default}
                    onCheckedChange={(v) => setEditing({ ...editing, is_default: v })}
                  />
                  <Label>Marcar como certificado por defecto</Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'a' | 'b')}>
          <TabsList>
            <TabsTrigger value="a">Diploma A</TabsTrigger>
            <TabsTrigger value="b">Diploma B (opcional)</TabsTrigger>
          </TabsList>
          <TabsContent value="a">
            <DiplomaEditor
              letter="A"
              value={editing.diploma_a}
              onChange={(next) => setEditing({ ...editing, diploma_a: next })}
              skillOptions={skillOptions}
              uploadImage={uploadDiplomaImage}
            />
          </TabsContent>
          <TabsContent value="b">
            <DiplomaEditor
              letter="B"
              value={editing.diploma_b}
              onChange={(next) => setEditing({ ...editing, diploma_b: next })}
              skillOptions={skillOptions}
              uploadImage={uploadDiplomaImage}
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ── List view ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Award className="w-5 h-5" /> Certificados de la ruta
          </h3>
          <p className="text-sm text-muted-foreground">
            Cada certificado puede emitir hasta 2 diplomas (A y B) con requisitos distintos.
          </p>
        </div>
        <Button onClick={handleNew}>
          <Plus className="w-4 h-4 mr-2" /> Nuevo certificado
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Cargando…</div>
          ) : certificates.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No hay certificados. Crea el primero con el botón "Nuevo certificado".
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre interno</TableHead>
                  <TableHead>Diploma A</TableHead>
                  <TableHead>Diploma B</TableHead>
                  <TableHead>Por defecto</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificates.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.private_name || '—'}</TableCell>
                    <TableCell className="text-sm">{(c as any).diploma_a_name || '—'}</TableCell>
                    <TableCell className="text-sm">{(c as any).diploma_b_name || '—'}</TableCell>
                    <TableCell>
                      {c.is_default ? (
                        <Badge className="bg-primary/10 text-primary border-primary/20 gap-1">
                          <Star className="w-3 h-3" /> Por defecto
                        </Badge>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleMakeDefault(c)}>
                          <Star className="w-3 h-3 mr-1" /> Marcar
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(c)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(c.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {confirmDelete && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setConfirmDelete(null)}>
          <Card className="max-w-md w-full m-4" onClick={e => e.stopPropagation()}>
            <CardHeader><CardTitle>Eliminar certificado</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Esta acción es permanente. Las cohortes que tengan este certificado asignado quedarán sin certificado.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
                <Button variant="destructive" onClick={handleDelete} disabled={deleteCert.isPending}>
                  {deleteCert.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
